<template>
  <vue-final-modal
    classes="flex justify-center items-center"
    v-model="modalVisible"
  >
    <div class="bg-white rounded-lg shadow-lg w-96 max-w-md mx-4">
      <div class="px-4 py-3 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">Add new version</h3>
        <p class="text-xs text-gray-500 mt-1">
          Duplicating from <span class="font-medium">{{ frozenSourceVersion }}</span>
        </p>
      </div>

      <div class="px-4 py-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            New version
          </label>
          <input
            type="text"
            v-model.trim="newVersion"
            placeholder="e.g. 1.1"
            :disabled="submitting"
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
            :class="{ 'border-red-400': versionError }"
            @keyup.enter="handleSubmit"
          />
          <p v-if="versionError" class="text-xs text-red-600 mt-1">{{ versionError }}</p>
          <p v-else class="text-xs text-gray-500 mt-1">
            Format: X.Y. Must be greater than {{ currentMax || 'any existing version' }}.
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Why are you creating this version? <span class="text-red-500">*</span>
          </label>
          <textarea
            :key="textareaKey"
            v-model.trim="note"
            rows="3"
            placeholder="Describe what this version is for..."
            :disabled="submitting"
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
            :class="{ 'border-red-400': noteError }"
          ></textarea>
          <p v-if="noteError" class="text-xs text-red-600 mt-1">{{ noteError }}</p>
        </div>
      </div>

      <div class="px-4 py-3 border-t border-gray-200 flex justify-end space-x-2">
        <button
          @click="handleCancel"
          :disabled="submitting"
          class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:hover:bg-gray-100 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          @click="handleSubmit"
          :disabled="submitting || !canSubmit"
          class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300 disabled:cursor-not-allowed"
        >
          <i v-if="submitting" class="fa-solid fa-circle-notch fa-spin text-[11px]"></i>
          {{ submitting ? 'Creating...' : 'Create version' }}
        </button>
      </div>
    </div>
  </vue-final-modal>
</template>

<script>
import {
  isValidVersionFormat,
  compareVersions,
  getMaxVersion,
  suggestNextVersion,
} from '../utilities/versionUtils'

export default {
  name: 'AddVersionModal',
  props: {
    isVisible: { type: Boolean, default: false },
    existingVersions: { type: Array, default: () => [] },
    sourceVersion: { type: String, default: '' },
    submitting: { type: Boolean, default: false },
  },
  data() {
    return {
      newVersion: '',
      note: '',
      noteTouched: false,
      textareaKey: 0,
      frozenSourceVersion: '',
    }
  },
  computed: {
    modalVisible: {
      get() { return this.isVisible },
      set(v) { this.$emit('update:isVisible', v) },
    },
    currentMax() {
      return getMaxVersion(this.existingVersions)
    },
    versionError() {
      if (!this.newVersion) return 'Version is required'
      if (!isValidVersionFormat(this.newVersion)) return 'Use X.Y format (e.g. 1.0, 2.3)'
      if (this.currentMax && compareVersions(this.newVersion, this.currentMax) <= 0) {
        return `Must be greater than ${this.currentMax}`
      }
      return ''
    },
    noteError() {
      if (!this.noteTouched) return ''
      if (!this.note) return 'A note is required'
      return ''
    },
    canSubmit() {
      return !this.versionError && this.note.length > 0
    },
  },
  watch: {
    isVisible(v) {
      if (v) {
        this.newVersion = suggestNextVersion(this.existingVersions) || ''
        this.note = ''
        this.noteTouched = false
        this.textareaKey += 1
        this.frozenSourceVersion = this.sourceVersion
      }
    },
  },
  methods: {
    handleCancel() {
      this.$emit('update:isVisible', false)
      this.$emit('cancel')
    },
    handleSubmit() {
      this.noteTouched = true
      if (!this.canSubmit) return
      this.$emit('confirm', { newVersion: this.newVersion, note: this.note })
    },
  },
}
</script>
